import { createWithEqualityFn } from 'zustand/traditional';
import { shallow } from 'zustand/shallow';
import { toast } from 'react-hot-toast';

import api from '../shared/api/cliente';
import {
  CLAVES, guardar, registrar, usuarioGuardado, haySesion, limpiarSesion,
} from './auth/almacenamientoSesion';
import { mensajeDeError } from './auth/mensajesError';

/**
 * Sesión del usuario.
 *
 * Ola 10, punto 10-20: eran 370 líneas con el envoltorio de `localStorage`, la
 * traducción de errores y una veintena de trazas de consola, la mitad de ellas
 * sin guarda de depuración: en producción volcaban el correo, el rol y la
 * respuesta completa del perfil.
 */

// Cortes de espera. El de cierre es más corto: si el servidor no responde, la
// sesión se cierra igual en local y no debe hacerse esperar al usuario.
const ESPERA_CIERRE_MS = 5000;
const ESPERA_PERFIL_MS = 10000;

/** Petición con corte de espera propio, sin dejar el temporizador colgado. */
const conEspera = async (peticion, milisegundos) => {
  const control = new AbortController();
  const temporizador = setTimeout(() => control.abort(), milisegundos);
  try {
    return await peticion(control.signal);
  } finally {
    clearTimeout(temporizador);
  }
};

const estadoInicial = () => ({
  user: usuarioGuardado(),
  isAuthenticated: haySesion(),
  isLoading: false,
  isInitialized: false,
  lastAuthCheck: null,
  authError: null,
});

const sesionCerrada = () => ({
  user: null,
  isAuthenticated: false,
  isLoading: false,
  isInitialized: true,
  authError: null,
});

export const createAuthSlice = (set, get) => ({
  ...estadoInicial(),

  /** Recupera la sesión guardada al arrancar la aplicación. */
  initialize: () => {
    const user = usuarioGuardado();

    set({
      user,
      isAuthenticated: haySesion(),
      isInitialized: true,
      isLoading: false,
      lastAuthCheck: Date.now(),
      authError: null,
    });

    registrar('AuthStore inicializado', { hayUsuario: Boolean(user) });
  },

  login: async (email, password) => {
    set({ isLoading: true, authError: null });

    try {
      const respuesta = await api.post('/api/auth/login', { email, password });
      if (!respuesta.data.success) {
        throw new Error(respuesta.data.error || 'Respuesta inesperada del servidor.');
      }

      const { user, accessToken } = respuesta.data.data;

      // Sin esta comprobación, una respuesta incompleta dejaría la aplicación
      // "autenticada" con un usuario a medias.
      if (!accessToken || !user?.email || !user?.id) {
        throw new Error('La respuesta del servidor está incompleta.');
      }

      guardar(CLAVES.usuario, JSON.stringify(user));
      guardar(CLAVES.token, accessToken);

      set({
        user,
        isAuthenticated: true,
        isLoading: false,
        isInitialized: true,
        lastAuthCheck: Date.now(),
        authError: null,
      });

      toast.success(`Bienvenido, ${user.firstName || user.email.split('@')[0]}`);
      return { success: true, user };
    } catch (error) {
      const mensaje = mensajeDeError(error);

      limpiarSesion();
      set({ ...sesionCerrada(), authError: mensaje });

      toast.error(mensaje);
      // Se relanza para que el formulario pueda reaccionar.
      throw new Error(mensaje);
    }
  },

  /**
   * Cierra la sesión. La limpieza local ocurre pase lo que pase: si el servidor
   * no responde, dejar la sesión abierta en el navegador sería peor.
   */
  logout: async () => {
    set({ isLoading: true, authError: null });

    try {
      await conEspera(
        (signal) => api.post('/api/auth/logout', {}, { signal }),
        ESPERA_CIERRE_MS
      );
    } catch {
      // Sin aviso al usuario: la sesión se cierra igualmente y un error aquí
      // solo confundiría.
      registrar('AuthStore: el cierre de sesion en el servidor no completo');
    } finally {
      limpiarSesion();
      set(sesionCerrada());

      if (typeof window !== 'undefined') {
        // El pequeño retardo deja que React aplique el estado antes de navegar.
        setTimeout(() => { window.location.href = '/'; }, 100);
      }
    }
  },

  /** Comprueba contra el servidor que la sesión guardada sigue siendo válida. */
  checkAuth: async () => {
    if (!haySesion()) {
      set({ ...sesionCerrada(), lastAuthCheck: Date.now() });
      return;
    }

    set({ isLoading: true, authError: null });

    try {
      const respuesta = await conEspera(
        (signal) => api.get('/api/auth/profile', { signal }),
        ESPERA_PERFIL_MS
      );

      const usuario = respuesta.data.data;
      if (!usuario?.id) throw new Error('El perfil recibido no es válido.');

      guardar(CLAVES.usuario, JSON.stringify(usuario));
      set({
        user: usuario,
        isAuthenticated: true,
        isLoading: false,
        isInitialized: true,
        lastAuthCheck: Date.now(),
        authError: null,
      });
    } catch (error) {
      // Un corte de espera no significa sesión inválida: puede ser la red. Se
      // deja la sesión como está en lugar de echar al usuario.
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        set({ isLoading: false, authError: 'No se pudo verificar la sesión.' });
        return;
      }

      await get().logout();
    }
  },

  /** Vuelve a pedir el perfil, por ejemplo tras editar los datos propios. */
  refreshUser: async () => {
    if (!get().isAuthenticated) return undefined;

    const respuesta = await api.get('/api/auth/profile');
    const usuario = respuesta.data.data;

    guardar(CLAVES.usuario, JSON.stringify(usuario));
    set({ user: usuario, lastAuthCheck: Date.now(), authError: null });

    return usuario;
  },

  clearAuthError: () => set({ authError: null }),
});

export const useAuthStore = createWithEqualityFn(createAuthSlice, shallow);
